import React, { ReactElement, useCallback, useMemo } from 'react'
import { Flex, Text } from 'theme-ui'
import { Button, Svg, TrayRenderProps, useTrayPresenter } from 'ui'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { CustomConfigurationItem } from 'components/FreeDns/sections/CustomConfiguration'
import pick from 'lodash/pick'
import ArrowRight from 'images/freeDNS/arrow-right.svg'
import { navigate } from 'gatsby'
import FilterDescriptionTrayContent from 'components/FreeDNSPage/TrayContents/FilterDescriptionTrayContent'
import { setSelectedFilter } from 'store/filters'
import { useGetFreeDNSFiltersQuery } from 'store/api/freeDns'
import CustomConfigIcon from 'images/freeDNS/custom-config.svg'
import { freeDnsPagePath } from 'components/FreeDns'

const CustomConfiguration = (): ReactElement => {
  const dispatch = useAppDispatch()
  const selectedFiltersPK = useAppSelector(s => s.freeDNS.selectedFiltersPKs)
  const { data: freeDnsData } = useGetFreeDNSFiltersQuery('')

  const filterTrayInfo = useMemo(
    () => ({
      id: `FilterTray`,
      renderTray: (props: TrayRenderProps) => <FilterDescriptionTrayContent {...props} />,
      zIndexName: 'zIndex550',
    }),
    [],
  )
  const { showTray: showFilterlistTray } = useTrayPresenter(filterTrayInfo)

  const isSelectedFilter = useCallback(
    PK => {
      return !!selectedFiltersPK?.includes(PK)
    },
    [selectedFiltersPK],
  )

  const sortedPremiumFilters = useMemo(
    () =>
      freeDnsData?.filters
        ?.filter(item => item?.['is_premium'])
        ?.sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [freeDnsData?.filters],
  )

  const sortedNotPremiumFilters = useMemo(
    () =>
      freeDnsData?.filters
        ?.filter(item => !item?.['is_premium'])
        ?.sort((a, b) => a.name.localeCompare(b.name)) ?? [],
    [freeDnsData?.filters],
  )

  return (
    <Flex
      sx={{
        position: 'relative',
        justifyContent: 'center',
        width: '100%',
      }}
    >
      <Flex
        className="ios-scrolling-glitches-fix"
        sx={{
          position: 'absolute',
          width: '100%',
          height: ['16.3rem', '32rem'],
          left: 0,
          top: ['10rem', '14rem'],
          backgroundColor: ['aquamarine15', 'aquamarine8'],
          filter: ['blur(75px)', 'blur(150px)'],
        }}
      />
      <Flex
        sx={{
          width: ['100%', '64.8rem', '99.6rem'],
          mt: ['6.4rem', '10rem', '10rem'],
          flexDirection: 'column',
          px: ['1.2rem', 0],
          textAlign: 'center',
        }}
      >
        <Flex sx={{ justifyContent: 'center', mb: '1.6rem' }}>
          <Svg
            svg={CustomConfigIcon}
            descriptiveText="Custom configuration icon"
            sx={{ justifyContent: 'center' }}
          />
        </Flex>
        <Flex
          sx={{
            flexDirection: 'column',
            alignItems: 'center',
            gap: ['0.8rem', '0.4rem'],
            maxWidth: '54rem',
            margin: '0 auto',
          }}
        >
          <Text
            variant="size40Weight600"
            sx={{
              color: 'aliceBlue',
              fontSize: ['2.8rem', '4rem'],
            }}
            as="h2"
            tabIndex={0}
          >
            Custom Configuration
          </Text>
          <Text
            variant="size16Weight400"
            sx={{
              color: 'lightSteelBlue',
              lineHeight: '140%',
              maxWidth: '48rem',
            }}
            tabIndex={0}
          >
            Don’t see the configuration you want? Create a custom one below by selecting the filters
            you require.
          </Text>
        </Flex>
        <Flex
          sx={{
            flexDirection: 'column',
            mt: ['4rem', '8rem'],
            zIndex: 'zIndex1',
          }}
        >
          <Flex
            sx={{
              flexWrap: 'wrap',
              gap: ['1.6rem', '4.8rem'],
              justifyContent: 'center',
              px: 0,
              mb: ['6.4rem', '10rem'],
            }}
          >
            {sortedNotPremiumFilters.map(filter => (
              <CustomConfigurationItem
                key={filter.PK}
                isSelected={isSelectedFilter(filter.PK)}
                isPremium={filter?.['is_premium']}
                {...pick(filter, 'name', 'PK', 'description')}
                openBlockList={() => {
                  dispatch(setSelectedFilter(filter))
                  showFilterlistTray()
                }}
              />
            ))}
          </Flex>

          <Flex
            data-testid="premium-filters-header"
            sx={{
              width: '100%',
              mx: 'auto',
              mb: ['2.4rem', '4.8rem'],
              mt: ['6.4rem', '0rem'],
              justifyContent: 'space-between',
              flexDirection: ['column', 'row'],
              alignItems: 'center',
            }}
          >
            <Text
              variant="size40Weight600"
              sx={{
                color: 'aliceBlue',
                fontSize: ['2.8rem', '4rem'],
              }}
              tabIndex={0}
              as="h2"
            >
              Premium Filters
            </Text>
            <Button
              ariaLabel="see plans"
              variant="roundedSecondary"
              sx={{
                width: ['fit-content'],
                height: '4.9rem',
                mt: ['2.4rem', 0],
              }}
              onClick={() => navigate('/plans', { state: { prevPage: freeDnsPagePath } })}
            >
              <Text
                variant="size17Weight600Line138"
                sx={{
                  color: 'white',
                  fontWeight: 500,
                  mr: '0.8rem',
                }}
              >
                See Plans
              </Text>
              <Svg svg={ArrowRight} descriptiveText="Navigate to plans page" fill={'white'} />
            </Button>
          </Flex>
          <Flex
            sx={{
              flexWrap: 'wrap',
              gap: ['1.6rem', '4.8rem'],
              justifyContent: 'center',
              px: 0,
              mb: ['6.4rem', '10rem'],
            }}
          >
            {sortedPremiumFilters.map(filter => (
              <CustomConfigurationItem
                key={filter.PK}
                isSelected={isSelectedFilter(filter.PK)}
                isPremium={filter?.['is_premium']}
                {...pick(filter, 'name', 'PK', 'description')}
                openBlockList={() => {
                  dispatch(setSelectedFilter(filter))
                  showFilterlistTray()
                }}
              />
            ))}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}

export default CustomConfiguration
